import { httpClient } from "../../../lib/http/axios";
import type { ApiEnvelope } from "../../../lib/http/contracts";
import {
  mapCustomerAssignmentConfirmPayload,
  mapCustomerAssignmentConfirmResult,
  mapCustomerAssignmentRevokePayload,
  mapCustomerWorkbenchContactListPage,
  mapCustomerWorkbenchDetail,
  mapCustomerWorkbenchFormPayload,
  mapCustomerWorkbenchItem,
  mapCustomerWorkbenchListQueryParams,
  mapCustomerWorkbenchMetadata,
  mapCustomerWorkbenchNlQueryParsePayload,
  mapCustomerWorkbenchNlQueryParseResult,
  mapCustomerWorkbenchOrganizationOption,
  mapCustomerWorkbenchOrganizationUnitOption,
  type CustomerWorkbenchAssignmentConfirmResultRaw,
  type CustomerWorkbenchContactListPageRaw,
  type CustomerWorkbenchDeleteResultRaw,
  type CustomerWorkbenchDetailRaw,
  type CustomerWorkbenchListPageRaw,
  type CustomerWorkbenchMetadataRaw,
  type CustomerWorkbenchNlQueryParseResultRaw,
  type CustomerWorkbenchOrganizationOptionRaw,
  type CustomerWorkbenchOrganizationUnitOptionRaw,
} from "../api/adapters";
import type {
  CustomerAssignmentConfirmPayload,
  CustomerAssignmentConfirmResult,
  CustomerAssignmentRevokePayload,
  CustomerWorkbenchContactListPage,
  CustomerWorkbenchDetail,
  CustomerWorkbenchFormPayload,
  CustomerWorkbenchListPage,
  CustomerWorkbenchListParams,
  CustomerWorkbenchMetadata,
  CustomerWorkbenchNlQueryParseResult,
  CustomerWorkbenchOrganizationOption,
  CustomerWorkbenchOrganizationUnitOption,
} from "../types";

/** 查询客户工作台元数据。 */
export async function fetchCustomerWorkbenchMetadata(): Promise<CustomerWorkbenchMetadata> {
  const response = await httpClient.get<ApiEnvelope<CustomerWorkbenchMetadataRaw>>(
    "/crm/customer-workbench/metadata",
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("客户工作台元数据接口返回了无效数据");
  }
  return mapCustomerWorkbenchMetadata(response.data.data);
}

/** 查询客户工作台分页列表。 */
export async function fetchCustomerWorkbenchList(
  params: CustomerWorkbenchListParams,
): Promise<CustomerWorkbenchListPage> {
  const response = await httpClient.get<ApiEnvelope<CustomerWorkbenchListPageRaw>>(
    "/crm/customer-workbench/customers",
    {
      params: mapCustomerWorkbenchListQueryParams(params),
    },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("客户工作台列表接口返回了无效数据");
  }
  return {
    items: (response.data.data.items ?? []).map(mapCustomerWorkbenchItem),
    total: response.data.data.total,
    page: response.data.data.page,
    pageSize: response.data.data.page_size,
  };
}

/** 解析客户工作台自然语言查询。 */
export async function parseCustomerWorkbenchNlQuery(
  query: string,
): Promise<CustomerWorkbenchNlQueryParseResult> {
  const response = await httpClient.post<ApiEnvelope<CustomerWorkbenchNlQueryParseResultRaw>>(
    "/crm/customer-workbench/nl-query/parse",
    mapCustomerWorkbenchNlQueryParsePayload(query),
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("客户智能查询解析接口返回了无效数据");
  }
  return mapCustomerWorkbenchNlQueryParseResult(response.data.data);
}

/** 确认客户主销售/共享销售分配。 */
export async function confirmCustomerAssignment(
  payload: CustomerAssignmentConfirmPayload,
): Promise<CustomerAssignmentConfirmResult> {
  const response = await httpClient.post<
    ApiEnvelope<CustomerWorkbenchAssignmentConfirmResultRaw>
  >(
    "/crm/customer-workbench/customers/assignment/confirm",
    mapCustomerAssignmentConfirmPayload(payload),
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("客户分配确认接口返回了无效数据");
  }
  return mapCustomerAssignmentConfirmResult(response.data.data);
}

/** 撤销客户共享销售关系。 */
export async function revokeCustomerSharedOwner(
  payload: CustomerAssignmentRevokePayload,
): Promise<CustomerAssignmentConfirmResult> {
  const response = await httpClient.post<
    ApiEnvelope<CustomerWorkbenchAssignmentConfirmResultRaw>
  >(
    "/crm/customer-workbench/customers/revoke-shared-owner",
    mapCustomerAssignmentRevokePayload(payload),
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("撤销客户共享销售接口返回了无效数据");
  }
  return mapCustomerAssignmentConfirmResult(response.data.data);
}

/** 查询客户关联联系人抽屉列表。 */
export async function fetchCustomerWorkbenchContacts({
  customerId,
  page,
  pageSize,
}: {
  customerId: number;
  page: number;
  pageSize: number;
}): Promise<CustomerWorkbenchContactListPage> {
  const response = await httpClient.get<ApiEnvelope<CustomerWorkbenchContactListPageRaw>>(
    `/crm/customer-workbench/customers/${customerId}/contacts`,
    {
      params: {
        page,
        page_size: pageSize,
      },
    },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("客户联系人列表接口返回了无效数据");
  }
  return mapCustomerWorkbenchContactListPage(response.data.data);
}

/** 查询客户详情。 */
export async function fetchCustomerWorkbenchDetail(
  customerId: number,
): Promise<CustomerWorkbenchDetail> {
  const response = await httpClient.get<ApiEnvelope<CustomerWorkbenchDetailRaw>>(
    `/crm/customer-workbench/customers/${customerId}`,
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("客户详情接口返回了无效数据");
  }
  return mapCustomerWorkbenchDetail(response.data.data);
}

/** 新增客户。 */
export async function createCustomerWorkbenchCustomer(
  payload: CustomerWorkbenchFormPayload,
): Promise<CustomerWorkbenchDetail> {
  const response = await httpClient.post<ApiEnvelope<CustomerWorkbenchDetailRaw>>(
    "/crm/customer-workbench/customers",
    mapCustomerWorkbenchFormPayload(payload),
    { timeout: 180_000, "axios-retry": { retries: 0 } },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("客户新增接口返回了无效数据");
  }
  return mapCustomerWorkbenchDetail(response.data.data);
}

/** 更新客户。 */
export async function updateCustomerWorkbenchCustomer({
  customerId,
  payload,
}: {
  customerId: number;
  payload: CustomerWorkbenchFormPayload;
}): Promise<CustomerWorkbenchDetail> {
  const response = await httpClient.put<ApiEnvelope<CustomerWorkbenchDetailRaw>>(
    `/crm/customer-workbench/customers/${customerId}`,
    mapCustomerWorkbenchFormPayload(payload),
    { timeout: 180_000, "axios-retry": { retries: 0 } },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("客户更新接口返回了无效数据");
  }
  return mapCustomerWorkbenchDetail(response.data.data);
}

/** 停用客户。 */
export async function disableCustomerWorkbenchCustomer(customerId: number): Promise<boolean> {
  const response = await httpClient.post<ApiEnvelope<CustomerWorkbenchDeleteResultRaw>>(
    `/crm/customer-workbench/customers/${customerId}/disable`,
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("客户停用接口返回了无效数据");
  }
  return response.data.data.deleted;
}

/** 启用客户。 */
export async function enableCustomerWorkbenchCustomer(customerId: number): Promise<boolean> {
  const response = await httpClient.post<ApiEnvelope<CustomerWorkbenchDeleteResultRaw>>(
    `/crm/customer-workbench/customers/${customerId}/enable`,
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("客户启用接口返回了无效数据");
  }
  return response.data.data.deleted;
}

/** 删除客户；当前后端以软删除口径处理。 */
export async function deleteCustomerWorkbenchCustomer(customerId: number): Promise<boolean> {
  const response = await httpClient.delete<ApiEnvelope<CustomerWorkbenchDeleteResultRaw>>(
    `/crm/customer-workbench/customers/${customerId}`,
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("客户删除接口返回了无效数据");
  }
  return response.data.data.deleted;
}

/** 搜索客户编辑可选择的学校。 */
export async function searchCustomerWorkbenchOrganizations({
  keyword,
  limit = 20,
}: {
  keyword?: string;
  limit?: number;
}): Promise<CustomerWorkbenchOrganizationOption[]> {
  const response = await httpClient.get<ApiEnvelope<CustomerWorkbenchOrganizationOptionRaw[]>>(
    "/crm/customer-workbench/organizations",
    {
      params: {
        keyword: normalizeOptionalText(keyword),
        limit,
      },
    },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("客户学校候选项接口返回了无效数据");
  }
  return response.data.data.map(mapCustomerWorkbenchOrganizationOption);
}

/** 搜索客户编辑可选择的院系专业。 */
export async function searchCustomerWorkbenchOrganizationUnits({
  orgId,
  keyword,
  limit = 50,
}: {
  orgId: number;
  keyword?: string;
  limit?: number;
}): Promise<CustomerWorkbenchOrganizationUnitOption[]> {
  const response = await httpClient.get<ApiEnvelope<CustomerWorkbenchOrganizationUnitOptionRaw[]>>(
    `/crm/customer-workbench/organizations/${orgId}/units`,
    {
      params: {
        keyword: normalizeOptionalText(keyword),
        limit,
      },
    },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("客户院系专业候选项接口返回了无效数据");
  }
  return response.data.data.map(mapCustomerWorkbenchOrganizationUnitOption);
}

/** 归一化可选查询文本。 */
function normalizeOptionalText(value?: string) {
  const normalized = value?.trim();
  return normalized ? normalized : undefined;
}
